import React, { useState, useRef, useEffect } from 'react';
import { Map, Share2, Settings, ClipboardList, Upload, ChevronLeft, ChevronRight, User, ShieldCheck, ChevronDown, ChevronUp, LogOut, Moon, HelpCircle, List, FileCheck, Table2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { apiService } from '../services/apiService';
import { useLogout } from '../hooks/useLogout';
import LogoutDialog from './LogoutDialog';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import {
  useListPortfoliosQuery,
  useLazyGetDefaultPortfolioQuery,
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useLazyGetPortfolioSiteQuery,
} from '../redux/api/portfolioApi';
import { setSelectedPortfolioId } from '../redux/slices/portfolioSlice';
import { setCurrentSite, setSitesForPortfolio } from '../redux/slices/siteSlice';
import type { PortfolioSite } from '../redux/api/portfolioApi';
import type { TenantInfo } from '../redux/slices/authSlice';
// Force rebuild

interface SidebarProps {
  onAddTab: (tabId: string) => void;
  onPopOutTab: (tabId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onAddTab, onPopOutTab, isCollapsed, onToggleCollapse }) => {
  const { sites, state, setSelectedSite, setSelectedComponents, refetchData, loading, error } = useAppContext();
  const [createSiteLoading, setCreateSiteLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [portfolioTabsOpen, setPortfolioTabsOpen] = useState(true);
  const [siteTabsOpen, setSiteTabsOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { handleLogout, isLoading: isLoggingOut } = useLogout();
  const dispatch = useAppDispatch();
  // Get profile for super/admin checks
  const profile = useAppSelector((state) => state.auth.profile);

  const userEmail = (profile.email || '').toLowerCase();
  const isPlatformSuperAdmin = userEmail.endsWith('@engramiq.com');
  
  // Get active tenant from profile (new structure)
  const activeTenant = (profile as any).active_tenant as TenantInfo | null | undefined;
  const tenants = ((profile as any).tenants as TenantInfo[] | null | undefined) || [];
  
  // Determine admin label and visibility based on new structure
  const activeTenantRole = activeTenant?.role;
  
  // Show admin button logic:
  // 1. If email ends with @engramiq.com → "Super Admin"
  // 2. If active_tenant.role is "super_admin" and email is NOT @engramiq.com → "Admin"
  // 3. If active_tenant.role is "tenant_admin" → "Admin"
  // 4. If active_tenant.role is "tenant_member" → Don't show
  const showAdminButton = isPlatformSuperAdmin || 
    (activeTenantRole === 'super_admin' && !isPlatformSuperAdmin) ||
    (activeTenantRole === 'tenant_admin');
  
  const adminLabel = isPlatformSuperAdmin ? 'Super Admin' : 'Admin';
  
  // For super admins, still use the platform super admin check
  const isSuperAdmin = isPlatformSuperAdmin;

  // Get current site from Redux state
  const currentSite = useAppSelector((state) => state.site.currentSite);
  
  // Get organization state for super admin organization selection
  const organizationState = useAppSelector((s) => s.organization);
  

  // Always fetch portfolios via RTK Query so data matches the current authenticated user
  const { data: portfoliosResponse, isLoading: portfoliosLoading, refetch: refetchPortfolios } = useListPortfoliosQuery(undefined, {
    // Ensure we refetch when the sidebar remounts (e.g. after login as a different user)
    refetchOnMountOrArgChange: true,
    skip: isSuperAdmin, // Super admins don't use portfolios in sidebar
  });
  const portfolios = portfoliosResponse?.items || [];
  
  // Get selected portfolio from Redux state
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);

  // For super admins, use currently selected organization (set from AdminViewTab)
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Super admin: fetch sites via /api/v1/admin/sites
  // - If an organization is selected: pass organization_id to filter by that org
  // - If no organization is selected: call without organization_id to load super admin's own sites
  const adminArgs = isSuperAdmin
    ? (currentOrgId ? { organization_id: currentOrgId } : undefined)
    : undefined;

  const {
    data: adminSitesResponse,
    isLoading: adminSitesLoading,
    refetch: refetchAdminSites,
  } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Other users: fetch sites for the selected portfolio via RTK Query
  const { data: portfolioSitesResponse, isLoading: portfolioSitesLoading } = useListPortfolioSitesQuery(
    selectedPortfolioId
      ? { portfolio_id: selectedPortfolioId }
      : // Value won't be used when skipped
        { portfolio_id: '' },
    {
      skip: isSuperAdmin || !selectedPortfolioId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  // Use admin sites for super admin, portfolio sites for others
  const allSites = isSuperAdmin
    ? (Array.isArray(adminSitesResponse)
        ? adminSitesResponse
        : (adminSitesResponse as any)?.items || []) as PortfolioSite[]
    : (Array.isArray(portfolioSitesResponse)
        ? portfolioSitesResponse
        : (portfolioSitesResponse as any)?.items || []) as PortfolioSite[];

  const portfolioSites = allSites;
  const sitesLoading = isSuperAdmin ? adminSitesLoading : portfolioSitesLoading;

  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();
  
  // Lazy query for getting default portfolio (used in async functions)
  const [triggerGetDefaultPortfolio] = useLazyGetDefaultPortfolioQuery();

  // Click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSiteSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Auto-select first portfolio if none selected
  useEffect(() => {
    if (!selectedPortfolioId && portfolios.length > 0) {
      dispatch(setSelectedPortfolioId(portfolios[0].id));
    }
  }, [dispatch, selectedPortfolioId, portfolios]);

  // Keep sites list in Redux for selected portfolio
  useEffect(() => {
    if (selectedPortfolioId && portfolioSites.length > 0) {
      dispatch(setSitesForPortfolio({ portfolioId: selectedPortfolioId, sites: portfolioSites }));
    }
  }, [dispatch, selectedPortfolioId, portfolioSites]);

  // Restore selected site from Redux state on mount/refresh
  useEffect(() => {
    if (currentSite?.site_id && !state.selectedSite && portfolioSites.length > 0) {
      // Check if the current site from Redux exists in the current portfolio's sites
      const siteExists = portfolioSites.some(site => site.site_id === currentSite.site_id);
      if (siteExists) {
        // If we have a current site in Redux that exists in the portfolio, restore it
        setSelectedSite(currentSite.site_id);
      }
    }
  }, [currentSite, state.selectedSite, setSelectedSite, portfolioSites]);

  const handleCreateSite = async () => {
    setCreateSiteLoading(true);
    try {
      // Get or create default portfolio
      const { data: portfolio } = await triggerGetDefaultPortfolio();
      
      if (!portfolio) {
        throw new Error('Failed to get or create default portfolio');
      }

      // Generate unique site name and code with timestamp
      const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '');
      const siteName = `New Solar Site ${timestamp}`;
      const siteCode = `SITE-${timestamp}`;

      const newSite = await apiService.createSite({
        site_name: siteName,
        site_code: siteCode,
        portfolio_id: portfolio.id,
        site_capacity_mw: 1.0,
        site_type: 'utility_scale',
        timezone: 'America/New_York'
      });

      // Refresh data to include new site
      await refetchData();

      // Select the new site
      setSelectedSite(newSite.site_id);

      alert(`Site "${newSite.site_name}" created successfully!`);
    } catch (error) {
      console.error('Error creating site:', error);
      alert(`Failed to create site: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreateSiteLoading(false);
    }
  };

  const handleCreateSiteWithAsBuilt = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file for the as-built document.');
      return;
    }

    // Navigate to Sites List tab immediately after file selection
    onAddTab('sitestable');

    setUploadLoading(true);
    try {
      // Get or create default portfolio
      const { data: portfolio } = await triggerGetDefaultPortfolio();
      
      if (!portfolio) {
        throw new Error('Failed to get or create default portfolio');
      }

      // Generate unique site name and code with timestamp
      const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '');
      const siteName = `New Site - ${file.name.replace('.pdf', '')}`;
      const siteCode = `SITE-${timestamp}`;

      const newSite = await apiService.createSite({
        site_name: siteName,
        site_code: siteCode,
        portfolio_id: portfolio.id,
        site_capacity_mw: 1.0,
        site_type: 'utility_scale',
        timezone: 'America/New_York'
      });

      // Upload the as-built document
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', newSite.site_id);
      formData.append('document_type_id', '3'); // As-built type
      formData.append('title', `As-Built - ${file.name.replace('.pdf', '')}`);
      formData.append('description', 'As-built document uploaded during site creation');
      formData.append('processor_type', 'as-built');

      await apiService.uploadDocument(formData);

      // Refresh data to include new site
      await refetchData();

      // Select the new site
      setSelectedSite(newSite.site_id);

      alert(`Site "${newSite.site_name}" created successfully with as-built document!`);

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating site with as-built:', error);
      alert(`Failed to create site with as-built: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const tabs = [
    // Active tabs - visible in sidebar
    { id: 'asbuilt', name: 'As-Built View', icon: ClipboardList, description: 'View as-built drawings and documentation' },
    { id: 'extractiondata', name: 'Extraction Data', icon: FileCheck, description: 'Validation results and drawing inconsistencies' },
    // { id: 'map', name: 'Map View', icon: Map, description: 'Satellite view with overlays' },
    { id: 'networkviewer', name: 'Network View', icon: Share2, description: 'Hierarchical component network' },
    { id: 'componentlist', name: 'Component View', icon: List, description: 'All components grouped by type' },
  ];

  return (
    <div className={`h-full bg-engramiq-dark-blue border-r border-engramiq-green/20 flex flex-col transition-all duration-300 relative ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Collapse/Expand Button */}
      <button
        onClick={onToggleCollapse}
        className="absolute top-14 -right-3 z-[9999] w-6 h-6 bg-engramiq-green hover:bg-engramiq-green/80 rounded-full flex items-center justify-center shadow-lg transition-all"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-engramiq-dark-blue" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-engramiq-dark-blue" />
        )}
      </button>

      {/* Logo Header */}
      <div className={`p-4 ${isCollapsed ? 'hidden' : ''}`}>
        <div className="flex items-center justify-center">
          <img
            src="/LOGO MARK AND TYPE - CMYK-02 (1).png"
            alt="Engramiq Logo"
            className="max-h-12 max-w-full object-contain"
          />
        </div>
      </div>

      {/* <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
        {isCollapsed ? (
          <div className="relative group flex justify-center">
            <button
              onClick={handleCreateSiteWithAsBuilt}
              disabled={uploadLoading || createSiteLoading}
              title="Upload As-Builts"
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className={`w-5 h-5 text-engramiq-green ${uploadLoading ? 'animate-spin' : ''}`} />
            </button>
            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
              Upload As-Builts
            </span>
          </div>
        ) : (
          <button
            onClick={handleCreateSiteWithAsBuilt}
            disabled={uploadLoading || createSiteLoading}
            title={uploadLoading ? "Creating site with as-built..." : "Upload As-Builts"}
            className="w-full px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className={`w-4 h-4 ${uploadLoading ? 'animate-spin' : ''}`} />
            Upload As-Builts
          </button>
        )}
      </div> */}

      <div className={`px-4 ${isCollapsed ? 'hidden' : ''}`}>
        <button
          onClick={() => setPortfolioTabsOpen(!portfolioTabsOpen)}
          className="w-full flex items-center justify-between mb-3 hover:text-engramiq-green transition-colors"
        >
          <h3 className="text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider">Portfolio Tabs</h3>
          {portfolioTabsOpen ? (
            <ChevronUp className="w-4 h-4 text-engramiq-bright-grey" />
          ) : (
            <ChevronDown className="w-4 h-4 text-engramiq-bright-grey" />
          )}
        </button>
        {portfolioTabsOpen && (<div className="space-y-2 relative">
          {/* Site selector (within selected portfolio) */}
          <div className="relative group" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full px-3 py-2 pr-8 bg-white/10 hover:bg-engramiq-green/20 rounded-lg text-engramiq-light-grey text-sm text-left focus:outline-none transition-all truncate"
              disabled={sitesLoading || portfolioSites.length === 0}
            >
              {currentSite?.site_name
                ? currentSite.site_name
                : sitesLoading
                  ? 'Loading sites...'
                  : portfolioSites.length === 0
                    ? 'No sites available'
                    : 'Select site'}
            </button>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-engramiq-green pointer-events-none z-10" />

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-surface-800 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden flex flex-col">
                <div className="p-2 border-b border-engramiq-green/20">
                  <input
                    type="text"
                    placeholder="Search sites..."
                    value={siteSearchTerm}
                    onChange={(e) => setSiteSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-900 rounded text-engramiq-light-grey text-sm focus:outline-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="overflow-y-auto">
                  {portfolioSites
                    .filter(site =>
                      siteSearchTerm === '' ||
                      site.site_name.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
                      site.site_code?.toLowerCase().includes(siteSearchTerm.toLowerCase())
                    )
                    .map(site => (
                      <button
                        key={site.site_id}
                        onClick={async () => {
                          // For super admins, get portfolio_id from the site object
                          // For regular users, use selectedPortfolioId
                          const portfolioIdToUse = isSuperAdmin 
                            ? (site.portfolio_id || (site as any).portfolio_id)
                            : selectedPortfolioId;
                          
                          if (!portfolioIdToUse) {
                            console.error('No portfolio ID available for site');
                            return;
                          }
                          
                          try {
                            const fullSite = await triggerGetPortfolioSite({
                              portfolio_id: portfolioIdToUse,
                              site_id: site.site_id,
                            }).unwrap();
                            // Store full site details in Redux
                            dispatch(setCurrentSite(fullSite));
                            // Also update global selectedSite in AppContext
                            setSelectedSite(fullSite.site_id);
                            setIsDropdownOpen(false);
                            setSiteSearchTerm('');
                          } catch (err) {
                            console.error('Failed to fetch site details', err);
                          }
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-engramiq-green/10 transition-colors ${
                          currentSite?.site_id === site.site_id
                            ? 'bg-engramiq-green/20 text-engramiq-light-grey'
                            : 'text-engramiq-light-grey'
                        }`}
                      >
                        <div className="font-medium">{site.site_name}</div>
                        {site.site_code && (
                          <div className="text-xs text-engramiq-bright-grey">
                            {site.site_code}
                          </div>
                        )}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />

          <button
            onClick={() => onAddTab('sitestable')}
            className="w-full px-3 py-2 bg-surface-800/50 hover:bg-white/10 rounded-lg text-sm text-engramiq-light-grey transition-all flex items-center gap-2 group"
          >
            <Table2 className="w-4 h-4 text-engramiq-green" />
            <span>Sites</span>
          </button>
        </div>)}
      </div>

      <div className={`flex-1 p-4 flex flex-col min-h-0 ${isCollapsed ? 'items-center gap-3 px-2' : ''}`}>
        {!isCollapsed && (
          <button
            onClick={() => setSiteTabsOpen(!siteTabsOpen)}
            className="w-full flex items-center justify-between mb-3 mt-4 hover:text-engramiq-green transition-colors"
          >
            <h3 className="text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider">Site Tabs</h3>
            {siteTabsOpen ? (
              <ChevronUp className="w-4 h-4 text-engramiq-bright-grey" />
            ) : (
              <ChevronDown className="w-4 h-4 text-engramiq-bright-grey" />
            )}
          </button>
        )}
        {(siteTabsOpen || isCollapsed) && (
          <div className={`${isCollapsed ? 'flex flex-col items-center gap-3 py-2' : 'flex-1 overflow-y-auto space-y-1 pr-1'}`}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <div
                  key={tab.id}
                  className={`${isCollapsed ? 'relative group' : 'relative group/container'}`}
                >
                  <button
                    onClick={() => onAddTab(tab.id)}
                    draggable={true}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('application/tab-id', tab.id);
                      e.dataTransfer.setData('text/plain', tab.name);
                    }}
                    className={`${isCollapsed
                      ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all'
                      : 'w-full text-left px-2 py-2 rounded-md bg-surface-800/50 hover:bg-white/10 transition-all group cursor-move'
                    }`}
                    title={tab.name}
                  >
                    <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'}`}>
                      <Icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-engramiq-green flex-shrink-0`} />
                      {!isCollapsed && (
                        <span className="text-sm font-medium text-engramiq-light-grey truncate">
                          {tab.name}
                        </span>
                      )}
                    </div>
                  </button>
                  {isCollapsed && (
                    <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
                      {tab.name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} mb-3 hover:text-engramiq-green transition-colors`}
          title={isCollapsed ? 'Settings' : undefined}
        >
          {!isCollapsed && (
            <h3 className="text-xs font-semibold text-engramiq-bright-grey uppercase tracking-wider">Settings</h3>
          )}
          {!isCollapsed ? (
            settingsOpen ? (
              <ChevronUp className="w-4 h-4 text-engramiq-bright-grey" />
            ) : (
              <ChevronDown className="w-4 h-4 text-engramiq-bright-grey" />
            )
          ) : (
            <Settings className="w-5 h-5 text-engramiq-green" />
          )}
        </button>
        {(settingsOpen || isCollapsed) && (
          <div className={`${isCollapsed ? 'flex flex-col items-center gap-3' : 'space-y-2'}`}>
            {/* Light/Dark Mode Toggle */}
            {/* <div className={`${isCollapsed ? 'relative group' : ''}`}>
              <div
                className={`${isCollapsed
                  ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60'
                  : 'flex items-center justify-between px-3 py-2 bg-surface-800/50 rounded-lg'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Moon className="w-4 h-4 text-engramiq-green" />
                  {!isCollapsed && <span className="text-sm text-engramiq-light-grey">Dark Mode</span>}
                </div>
                {!isCollapsed && (
                  <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-engramiq-green/30 transition-colors">
                    <span className="inline-block h-4 w-4 translate-x-6 transform rounded-full bg-engramiq-green transition-transform" />
                  </button>
                )}
              </div>
              {isCollapsed && (
                <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
                  Dark Mode
                </span>
              )}
            </div> */}

            {/* User Profile Button */}
            <div className={`${isCollapsed ? 'relative group' : ''}`}>
              <button
                onClick={() => onAddTab('userprofile')}
                className={`${isCollapsed
                  ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all'
                  : 'w-full text-left px-2 py-2 rounded-md bg-surface-800/50 hover:bg-white/10 transition-all group'
                }`}
                title="User Profile"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                  {!isCollapsed && <span className="text-sm text-engramiq-light-grey">My Profile</span>}
                </div>
              </button>
              {isCollapsed && (
                <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
                  My Profile
                </span>
              )}
            </div>

            {/* <div className={`${isCollapsed ? 'relative group' : ''}`}>
              <button
                onClick={() => onAddTab('support')}
                className={`${isCollapsed
                  ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all'
                  : 'w-full text-left px-2 py-2 rounded-md bg-surface-800/50 hover:bg-white/10 transition-all group'
                }`}
                title="Help & Support"
              >
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                  {!isCollapsed && <span className="text-sm text-engramiq-light-grey">Help & Support</span>}
                </div>
              </button>
              {isCollapsed && (
                <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
                  Help & Support
                </span>
              )}
            </div> */}

            {/* Administrators View Button */}
            {showAdminButton && (
              <div className={`${isCollapsed ? 'relative group' : ''}`}>
                <button
                  onClick={() => onAddTab('adminview')}
                  className={`${isCollapsed
                    ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all'
                    : 'w-full text-left px-2 py-2 rounded-md bg-surface-800/50 hover:bg-white/10 transition-all group'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                    {!isCollapsed && (
                      <span className="text-sm text-engramiq-light-grey">
                        {adminLabel}
                      </span>
                    )}
                  </div>
                </button>
                {isCollapsed && (
                  <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg z-50">
                    {adminLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
        <div className={`${isCollapsed ? 'relative group' : ''}`}>
          <button
            onClick={() => setShowLogoutDialog(true)}
            disabled={isLoggingOut}
            className={`${isCollapsed
              ? 'w-10 h-10 flex items-center justify-center rounded-lg bg-surface-800/60 hover:bg-white/10 transition-all'
              : 'w-full px-3 py-2 bg-surface-800/50 hover:bg-white/10 rounded-lg text-sm text-engramiq-light-grey transition-all flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            <LogOut className="w-4 h-4 text-engramiq-green" />
            {!isCollapsed && <span>{isLoggingOut ? 'Signing out...' : 'Logout'}</span>}
          </button>
          {isCollapsed && (
            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 rounded bg-surface-900 text-xs text-engramiq-light-grey whitespace-nowrap opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 shadow-lg">
              Logout
            </span>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={async () => {
          setShowLogoutDialog(false);
          await handleLogout();
        }}
        isLoading={isLoggingOut}
      />


    </div>
  );
};

export default Sidebar;